import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, CheckCircle2, type LucideIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageSEO } from "@/components/seo/PageSEO";
import { useLanguage } from "@/hooks/useLanguage";

interface Challenge {
  titleKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

interface Solution {
  titleKey: string;
  descriptionKey: string;
  icon: LucideIcon;
}

interface RelevantProduct {
  nameKey: string;
  descriptionKey: string;
  href: string;
  icon: LucideIcon;
}

interface SolutionPageProps {
  solutionKey: string;
  heroIcon: LucideIcon;
  challenges: Challenge[];
  solutions: Solution[];
  relevantProducts: RelevantProduct[];
  heroHighlights?: string[];
  challengeImpactKey?: string;
  productBullets?: Record<string, string[]>;
}

export function SolutionPageTemplate({
  solutionKey,
  heroIcon: HeroIcon,
  challenges,
  solutions,
  relevantProducts,
  heroHighlights: heroHighlightsProp,
  challengeImpactKey: challengeImpactProp,
  productBullets: productBulletsProp,
}: SolutionPageProps) {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguage();
  const s = `solutionPages.${solutionKey}`;
  const e = `solutionExtras.${solutionKey}`;

  // Pull localized extras from i18n; fall back to props for backwards-compat.
  const i18nHighlights = t(`${e}.heroHighlights`, { returnObjects: true }) as unknown;
  const heroHighlights = Array.isArray(i18nHighlights) ? (i18nHighlights as string[]) : heroHighlightsProp;

  const i18nImpact = t(`${e}.challengeImpact`);
  const challengeImpact = i18nImpact && i18nImpact !== `${e}.challengeImpact` ? i18nImpact : challengeImpactProp;

  const i18nBullets = t(`${e}.productBullets`, { returnObjects: true }) as unknown;
  const productBullets =
    i18nBullets && typeof i18nBullets === "object" && !Array.isArray(i18nBullets)
      ? (i18nBullets as Record<string, string[]>)
      : productBulletsProp;


  const serviceSchema = {
    "@type": "Service",
    name: t(`${s}.hero.title`),
    description: t(`${s}.hero.subtitle`),
    provider: {
      "@type": "Organization",
      name: "Geposit",
      url: "https://geposit.se",
    },
    areaServed: ["SE", "NO", "DK", "FI"],
  };

  return (
    <div>
      <PageSEO
        title={t(`${s}.hero.title`)}
        description={t(`seo.losningar${solutionKey.charAt(0).toUpperCase() + solutionKey.slice(1)}`)}
        structuredData={serviceSchema}
      />
      {/* Hero Section */}
      <section className="relative bg-primary text-primary-foreground py-12 lg:py-28 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-primary/80" />
        {/* Decorative elements */}
        <div className="absolute top-10 right-10 w-48 h-48 rounded-full border border-primary-foreground/5" />
        <div className="absolute bottom-0 left-10 w-72 h-72 rounded-full border border-primary-foreground/5 translate-y-1/3" />

        <div className="relative container mx-auto px-4 lg:px-8">
          <Link
            to={getLocalizedPath("/losningar")}
            className="inline-flex items-center text-sm text-primary-foreground/60 hover:text-primary-foreground transition-colors mb-8"
          >
            <ArrowLeft className="mr-1.5 h-4 w-4" />
            {t("solutionPages.backToSolutions")}
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-block text-sm font-bold font-subheading uppercase tracking-widest text-coral mb-3">
                {t(`${s}.hero.tagline`)}
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium font-heading leading-tight tracking-tight">
                {t(`${s}.hero.title`)}
              </h1>
              <p className="mt-6 text-lg md:text-xl text-primary-foreground/80 font-body leading-relaxed max-w-lg">
                {t(`${s}.hero.subtitle`)}
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <Button
                  size="lg"
                  asChild
                  className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
                >
                  <Link to={getLocalizedPath("/kontakt?type=demo")}>
                    {t("solutionPages.ctaButton")}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Link>
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  asChild
                  className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium text-base"
                >
                <Link to={getLocalizedPath("/tjanster")}>{t("solutionPages.ctaSecondary")}</Link>
                </Button>
              </div>
            </motion.div>

            {/* Highlight box instead of big icon */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="flex justify-center"
            >
              {heroHighlights && heroHighlights.length > 0 ? (
                <div className="bg-primary-foreground/10 backdrop-blur-sm rounded-2xl p-8 lg:p-10 w-full max-w-md">
                  <h3 className="text-lg font-bold font-subheading text-primary-foreground mb-6">
                    {t("illustrations.keyFactors")}
                  </h3>
                  <ul className="space-y-4">
                    {heroHighlights.map((highlight, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.5, delay: 0.4 + i * 0.15 }}
                        className="flex gap-3 items-start"
                      >
                        <CheckCircle2 className="h-5 w-5 text-coral flex-shrink-0 mt-0.5" />
                        <span className="text-primary-foreground/90 font-body leading-relaxed">
                          {highlight}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                </div>
              ) : (
                <div className="w-48 h-48 lg:w-64 lg:h-64 rounded-3xl bg-primary-foreground/10 backdrop-blur-sm flex items-center justify-center shadow-2xl">
                  <HeroIcon className="w-24 h-24 lg:w-32 lg:h-32 text-coral" />
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Challenges Section — split layout */}
      <section className="py-12 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 lg:gap-16">
            {/* Left: heading + description */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex-1 lg:max-w-md"
            >
              <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
                {t(`${s}.challenges.title`)}
              </h2>
              <p className="mt-4 text-lg text-muted-foreground font-body leading-relaxed">
                {t(`${s}.challenges.subtitle`)}
              </p>
              {challengeImpact && (
                <p className="mt-4 text-sm text-coral italic font-body leading-relaxed">
                  {challengeImpact}
                </p>
              )}

            </motion.div>

            {/* Right: challenge cards stacked */}
            <div className="flex-1 space-y-4">
              {challenges.map((challenge, index) => (
                <motion.div
                  key={challenge.titleKey}
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="flex gap-4 bg-card rounded-xl border border-border p-6"
                >
                  <div className="w-11 h-11 rounded-lg bg-coral/10 flex items-center justify-center flex-shrink-0">
                    <challenge.icon className="h-5 w-5 text-coral" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold font-subheading text-foreground mb-1">
                      {t(challenge.titleKey)}
                    </h3>
                    <p className="text-sm text-muted-foreground font-body leading-relaxed">
                      {t(challenge.descriptionKey)}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Solutions — vertical timeline */}
      <section className="py-12 lg:py-28 bg-secondary/30">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t(`${s}.solutions.title`)}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body">
              {t(`${s}.solutions.subtitle`)}
            </p>
          </motion.div>

          <div className="max-w-3xl mx-auto relative">
            {/* Vertical line — hidden on mobile */}
            <div className="hidden lg:block absolute left-7 top-4 bottom-4 w-px bg-coral/20" />

            {solutions.map((solution, index) => (
              <motion.div
                key={solution.titleKey}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.15 }}
                className="flex gap-5 lg:gap-6 mb-8 last:mb-0"
              >
                {/* Numbered circle */}
                <motion.div
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ type: "spring", stiffness: 300, damping: 20, delay: index * 0.15 + 0.1 }}
                  className="w-14 h-14 rounded-full bg-coral flex items-center justify-center flex-shrink-0 z-10"
                >
                  <span className="text-xl font-bold text-coral-foreground font-subheading">
                    {index + 1}
                  </span>
                </motion.div>

                {/* Content card */}
                <div className="flex-1 bg-card rounded-xl border border-border p-6">
                  <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                    {t(solution.titleKey)}
                  </h3>
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {t(solution.descriptionKey)}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Relevant Products — with accent line and bullets */}
      <section className="py-12 lg:py-28 bg-background">
        <div className="container mx-auto px-4 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-2xl mx-auto mb-14"
          >
            <h2 className="text-3xl md:text-4xl font-medium font-heading text-primary tracking-tight">
              {t(`${s}.products.title`)}
            </h2>
            <p className="mt-4 text-lg text-muted-foreground font-body">
              {t(`${s}.products.subtitle`)}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 max-w-5xl mx-auto">
            {relevantProducts.map((product, index) => {
              // Extract product slug from href for bullet lookup
              const productSlug = product.href.split("/").pop() || "";
              const bullets = productBullets?.[productSlug] || [];

              return (
                <motion.div
                  key={product.nameKey}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link
                    to={product.href}
                    className="group block h-full"
                  >
                    <div className="rounded-xl overflow-hidden border border-border hover:shadow-lg hover:border-coral/30 transition-all duration-300 h-full flex flex-col">
                      {/* Coral accent line */}
                      <div className="h-1.5 bg-gradient-to-r from-coral to-coral/60" />

                      <div className="p-7 flex flex-col flex-1">
                        <div className="w-11 h-11 rounded-lg bg-petrol-light flex items-center justify-center mb-4">
                          <product.icon className="h-5 w-5 text-primary dark:text-white" />
                        </div>
                        <h3 className="text-lg font-bold font-subheading text-foreground mb-2">
                          {t(product.nameKey)}
                        </h3>
                        <p className="text-sm text-muted-foreground font-body leading-relaxed mb-4">
                          {t(product.descriptionKey)}
                        </p>

                        {/* Bullet points */}
                        {bullets.length > 0 && (
                          <ul className="space-y-2 mb-4">
                            {bullets.map((bullet, bi) => (
                              <li key={bi} className="flex gap-2 text-xs">
                                <CheckCircle2 className="h-3.5 w-3.5 text-coral flex-shrink-0 mt-0.5" />
                                <span className="text-muted-foreground font-body">{bullet}</span>
                              </li>
                            ))}
                          </ul>
                        )}

                        <span className="mt-auto inline-flex items-center text-sm font-medium text-coral group-hover:gap-2 transition-all">
                          {t("solutionPages.readMore")} <ArrowRight className="ml-1 h-4 w-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 lg:py-28 bg-primary text-primary-foreground">
        <div className="container mx-auto px-4 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-xl mx-auto"
          >
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-medium font-heading tracking-tight">
              {t(`${s}.cta.title`)}
            </h2>
            <p className="mt-4 text-lg text-primary-foreground/70 font-body">
              {t(`${s}.cta.subtitle`)}
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                asChild
                className="bg-coral text-coral-foreground hover:bg-coral/90 font-semibold shadow-lg shadow-coral/25 text-base px-8"
              >
                <Link to={getLocalizedPath("/kontakt?type=demo")}>
                  {t("solutionPages.ctaButton")}
                  <ArrowRight className="ml-1 h-4 w-4" />
                </Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                asChild
                className="bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 font-medium text-base"
              >
                <Link to={getLocalizedPath("/tjanster")}>{t("solutionPages.ctaSecondary")}</Link>
              </Button>
            </div>
            <p className="text-sm text-primary-foreground/50 mt-6 font-body">
              {t("illustrations.joinCompanies")}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
